import { useState } from 'react'
import {
  Box,
  Button,
  Stack,
  Autocomplete,
  TextField,
  Badge,
  Drawer,
  Typography,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import CloseIcon from '@mui/icons-material/Close'
import TableColumnFilters, { FilterOption, MultiSelectFilter } from './TableColumnFilters'

export type { FilterOption, MultiSelectFilter }

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface AutocompleteFilter<T = any> {
  name: string
  label: string
  type: 'autocomplete'
  options: T[]
  value: T | null
  getOptionLabel?: (option: T) => string
  isOptionEqualToValue?: (option: T, value: T) => boolean
  placeholder?: string
}

export type FilterBarFilter = MultiSelectFilter | AutocompleteFilter

interface FilterBarProps {
  filters?: FilterBarFilter[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (filterName: string, nextValue: any) => void
  onClearAll?: () => void
  /** Rows currently showing. Only used to label the phone sheet's close button. */
  resultCount?: number
}

/**
 * Shared list-page filter bar — adapted from CrowCase (common/table/FilterBar.jsx).
 *
 * Deviation from CrowCase: filters are ALWAYS VISIBLE from 768px up. CrowCase
 * hides them behind a collapsed "Filters" toggle; per product direction we
 * render the selectors inline so the available filters are always discoverable.
 * Selected values show as removable chips inside each Select (via
 * TableColumnFilters), so no separate collapsed-summary chip row is needed.
 *
 * Below 768px that inline bar costs roughly 250px of a 844px screen before any
 * data appears, so there it becomes a button plus a bottom sheet. The rule is
 * preserved rather than broken: the filters were always visible at every width
 * that had a designed layout, and they still are. This only defines a width
 * that previously had none. The button carries the active-filter count, so the
 * fact that filters are applied never goes behind the click — only the
 * controls do.
 *
 * Two filter shapes are supported, mixed freely:
 *   1. Multi-select (closed enum): { name, label, options: [{value,label}], value: string[] }
 *   2. Autocomplete (typeahead): { name, label, type: 'autocomplete', options, value, ... }
 *
 * NOTE: CrowCase styles the panel with M3 surface tokens. Missfit's theme has none,
 * so the background falls back to background.paper.
 */
export default function FilterBar({ filters = [], onChange, onClearAll, resultCount }: FilterBarProps) {
  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down(768))
  const [sheetOpen, setSheetOpen] = useState(false)
  // Small fields are 37px, under the 44px touch-target floor the sheet has to
  // meet. The inline bar from 768 up keeps them, so its density is unchanged.
  const fieldSize = isPhone ? 'medium' : 'small'

  const isAutocomplete = (f: FilterBarFilter): f is AutocompleteFilter =>
    (f as AutocompleteFilter)?.type === 'autocomplete'
  const autocompleteFilters = filters.filter(isAutocomplete)
  const multiSelectFilters = filters.filter(
    (f): f is MultiSelectFilter => !isAutocomplete(f)
  )

  const hasActiveAutocomplete = autocompleteFilters.some((f) => f.value != null && f.value !== '')

  const activeCount =
    multiSelectFilters.filter((f) => (f.value?.length ?? 0) > 0).length +
    autocompleteFilters.filter((f) => f.value != null && f.value !== '').length

  if (filters.length === 0) return null

  // One wrapping row for every control, rather than a row of typeaheads stacked
  // above a row of selects. Split in two, the bar read as ragged whenever either
  // group was short: a lone company typeahead claimed a full line and left the
  // rest of the panel empty while the selects sat on the line below. Flowing
  // them all through a single wrap packs them left and lets the panel size to
  // what is actually there.
  const controls = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        flexWrap: 'wrap',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
      }}
    >
      {autocompleteFilters.length > 0 && (
        <>
          {autocompleteFilters.map((f) => (
            <Autocomplete
              key={f.name}
              size={fieldSize}
              options={f.options || []}
              value={f.value ?? null}
              onChange={(_, next) => onChange?.(f.name, next)}
              getOptionLabel={f.getOptionLabel || ((o) => o?.label || o?.name || '')}
              isOptionEqualToValue={f.isOptionEqualToValue}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={f.label}
                  placeholder={f.placeholder || `Filter by ${f.label.toLowerCase()}`}
                />
              )}
              sx={
                isPhone
                  ? {
                      width: '100%',
                      // The open and clear buttons are 28px by default. In the
                      // sheet they grow to 44px, so the input gives up the width
                      // they now take instead of running text beneath them.
                      '& .MuiAutocomplete-endAdornment': {
                        top: '50%',
                        transform: 'translateY(-50%)',
                      },
                      '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
                        width: 44,
                        height: 44,
                      },
                      '&.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root': { pr: '53px' },
                      '&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root': {
                        pr: '97px',
                      },
                    }
                  : { minWidth: 200, maxWidth: 260, flex: '1 1 auto' }
              }
            />
          ))}
        </>
      )}
      {(multiSelectFilters.length > 0 || (!isPhone && hasActiveAutocomplete)) && (
        <TableColumnFilters
          filters={multiSelectFilters}
          onChange={onChange}
          // The sheet's footer already carries a 44px "Clear all", so the
          // inline one would be a second, undersized copy of the same action.
          onClearAll={isPhone ? undefined : onClearAll}
          // An active autocomplete filter (e.g. the company typeahead) should
          // surface "Clear All" too, even with no multi-select value set —
          // and on a bar that has ONLY autocompletes (the networking table),
          // where this block would otherwise never render at all.
          hasAdditionalActiveFilters={hasActiveAutocomplete}
          size={fieldSize}
        />
      )}
    </Box>
  )

  if (isPhone) {
    return (
      <>
        <Box sx={{ mb: 2 }}>
          <Badge badgeContent={activeCount} color="primary">
            <Button
              variant="outlined"
              startIcon={<FilterAltOutlinedIcon />}
              onClick={() => setSheetOpen(true)}
              sx={{ minHeight: 44 }}
            >
              Filters
            </Button>
          </Badge>
        </Box>
        <Drawer
          anchor="bottom"
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          PaperProps={{
            // MuiDrawer's theme override paints paper gray900 for the
            // navigation rail. A content sheet that inherits it renders
            // unreadable, which is what MWA-253's defects were.
            sx: {
              backgroundColor: 'background.paper',
              color: 'text.primary',
              borderRadius: '18px 18px 0 0',
              maxHeight: '80vh',
            },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, pt: 2, pb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
              Filters
            </Typography>
            <IconButton aria-label="Close filters" onClick={() => setSheetOpen(false)} sx={{ minWidth: 44, minHeight: 44 }}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />
          <Box sx={{ px: 2, py: 2, overflowY: 'auto' }}>{controls}</Box>
          <Divider />
          <Stack direction="row" spacing={1} sx={{ p: 2 }}>
            <Button
              variant="outlined"
              onClick={onClearAll}
              disabled={activeCount === 0}
              sx={{ flexGrow: 1, minHeight: 44 }}
            >
              Clear all
            </Button>
            <Button
              variant="contained"
              onClick={() => setSheetOpen(false)}
              sx={{ flexGrow: 2, minHeight: 44 }}
            >
              {resultCount == null
                ? 'Done'
                : `Show ${resultCount} ${resultCount === 1 ? 'result' : 'results'}`}
            </Button>
          </Stack>
        </Drawer>
      </>
    )
  }

  return (
    <Box
      sx={(t) => ({
        px: { xs: 1.5, sm: 2.5 },
        py: { xs: 1, sm: 1.5 },
        mb: 2,
        backgroundColor:
          (t.palette as unknown as { surface?: { containerLow?: string } }).surface
            ?.containerLow ?? t.palette.background.paper,
        // sx treats numeric borderRadius as a multiplier of theme.shape.borderRadius,
        // so use a literal px string to skip the multiplier.
        borderRadius: `${(t.shape as unknown as { m3?: { medium?: number } })?.m3?.medium ?? 12}px`,
      })}
    >
      {controls}
    </Box>
  )
}
